package features.shifts.handlers

import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.HTMLSelectElement
import AppState
import Router
import shared.ui.ModalManager
import features.shifts.domain.ShiftTemplate
import features.shifts.data.ShiftRepository

/**
 * Handler for creating a new shift template
 */
fun createShift() {
    console.log("🔧 createShift() called")
    
    val teamId = AppState.currentTeam?.id
    if (teamId == null) {
        window.alert("No team selected")
        return
    }
    
    val codeInput = document.getElementById("shift-code-input") as? HTMLInputElement
    val startHourInput = document.getElementById("shift-start-hour") as? HTMLSelectElement
    val startMinuteInput = document.getElementById("shift-start-minute") as? HTMLSelectElement
    val endHourInput = document.getElementById("shift-end-hour") as? HTMLSelectElement
    val endMinuteInput = document.getElementById("shift-end-minute") as? HTMLSelectElement
    
    val code = codeInput?.value?.trim() ?: ""
    val startHour = startHourInput?.value ?: "00"
    val startMinute = startMinuteInput?.value ?: "00"
    val endHour = endHourInput?.value ?: "00"
    val endMinute = endMinuteInput?.value ?: "00"
    
    if (code.isEmpty()) {
        window.alert("Please enter a shift code")
        return
    }
    
    val startTime = "$startHour:$startMinute"
    val endTime = "$endHour:$endMinute"
    
    console.log("Creating shift:", code, "from", startTime, "to", endTime, "color:", selectedShiftColor)
    
    // Generate ID for new shift
    val shiftId = generateId()
    
    val shift = ShiftTemplate(
        id = shiftId,
        code = code,
        startTime = startTime,
        endTime = endTime,
        color = selectedShiftColor
    )
    
    ShiftRepository.createShift(teamId, shift)
        .then {
            console.log("✅ Shift created with ID:", shiftId)
            
            // Reload shifts
            ShiftRepository.loadShifts(teamId)
                .then { shifts: Array<ShiftTemplate> ->
                    AppState.shiftTemplates[teamId] = shifts.toMutableList()
                    
                    // Close modal and re-render
                    ModalManager.close()
                    Router.render()
                }
        }
        .catch { error ->
            console.error("❌ Error creating shift:", error)
            window.alert("Failed to create shift. Please try again.")
        }
}

/**
 * Handler for updating an existing shift template
 */
fun updateShift() {
    console.log("🔧 updateShift() called")
    
    val teamId = AppState.currentTeam?.id
    if (teamId == null) {
        window.alert("No team selected")
        return
    }
    
    val idInput = document.getElementById("edit-shift-id") as? HTMLInputElement
    val codeInput = document.getElementById("edit-shift-code") as? HTMLInputElement
    val startHourInput = document.getElementById("edit-shift-start-hour") as? HTMLSelectElement
    val startMinuteInput = document.getElementById("edit-shift-start-minute") as? HTMLSelectElement
    val endHourInput = document.getElementById("edit-shift-end-hour") as? HTMLSelectElement
    val endMinuteInput = document.getElementById("edit-shift-end-minute") as? HTMLSelectElement
    
    val shiftId = idInput?.value ?: ""
    val code = codeInput?.value?.trim() ?: ""
    val startHour = startHourInput?.value ?: "00"
    val startMinute = startMinuteInput?.value ?: "00"
    val endHour = endHourInput?.value ?: "00"
    val endMinute = endMinuteInput?.value ?: "00"
    
    if (shiftId.isEmpty() || code.isEmpty()) {
        window.alert("Invalid shift data")
        return
    }
    
    val startTime = "$startHour:$startMinute"
    val endTime = "$endHour:$endMinute"
    
    console.log("Updating shift:", shiftId, "code:", code, "color:", selectedEditShiftColor)
    
    val shift = ShiftTemplate(
        id = shiftId,
        code = code,
        startTime = startTime,
        endTime = endTime,
        color = selectedEditShiftColor
    )
    
    ShiftRepository.updateShift(teamId, shiftId, shift)
        .then {
            console.log("✅ Shift updated successfully")
            
            // Reload shifts
            ShiftRepository.loadShifts(teamId)
                .then { shifts: Array<ShiftTemplate> ->
                    AppState.shiftTemplates[teamId] = shifts.toMutableList()
                    
                    // Close modal and re-render
                    ModalManager.close()
                    Router.render()
                }
        }
        .catch { error ->
            console.error("❌ Error updating shift:", error)
            window.alert("Failed to update shift. Please try again.")
        }
}

// Helper function
private fun generateId(): String {
    return js("Date.now().toString() + Math.random().toString(36).substr(2, 9)") as String
}
